import React from 'react';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  RedAsterix,
  Grid,
  VStack,
  DateInput,
  DateInputNoYear,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { InsuranceDetailFormValues } from '../../../../../types/Form';
import AdvisoryReportTitleWithDescription from '../../../../shared/AdvisoryReportTitleWithDescription';
import LabeledSwitch from '../../../../../../../../../common/src/components/inputs/LabeledSwitch';
import { InsurancePolicyType } from '../../../../../../../types/AdvisoryReport';
import RiskObjectInputComponent from './RiskObjectInputComponent';
import InsuranceCompaniesInput from './InsuranceCompaniesInput';
import PrefillOfferButton from '../../../offers/PrefillOfferButton';
import { Change } from '../../../../../../../../../../types/Form';
import useRiskObjectInputStore from '@parties/src/features/risk-objects/store/riskObjectInputStore';

interface Props {
  type: InsurancePolicyType;
}

const AddInsuranceModalInsuranceFormGeneral: React.FC<Props> = ({ type }) => {
  const { t } = useTranslation();
  const setFocusedInput = useRiskObjectInputStore(
    (state) => state.setFocusedInput,
  );
  const {
    register,
    control,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<InsuranceDetailFormValues>();

  return (
    // Added key to force re-render when prefill is used (Because date inputs are uncontrolled)
    <VStack align="stretch" spacing="4" key={watch('coverages.0.id')}>
      <AdvisoryReportTitleWithDescription
        title={t(`ADVISORY-REPORTS_${type}_MODAL_DETAIL_TITLE`)}
        description={t(`ADVISORY-REPORTS_${type}_MODAL_DETAIL_DESCRIPTION`)}
        rightElement={type === 'OFFER' ? <PrefillOfferButton /> : undefined}
      />
      <Grid
        templateColumns={{ base: '1fr', tablet: 'repeat(2, 50%)' }}
        gridGap="4"
      >
        <FormControl isInvalid={!!errors.name}>
          <FormLabel>
            {t(`ADVISORY-REPORTS_${type}_MODAL_DETAIL_NAME`)} <RedAsterix />
          </FormLabel>
          <Input
            {...register('name', {
              onChange: errors.name ? () => trigger('name') : undefined,
              onBlur: () => setFocusedInput(null),
            })}
            onFocus={() => setFocusedInput('name')}
          />
          {errors.name?.message && (
            <FormErrorMessage>{t(errors.name.message)}</FormErrorMessage>
          )}
        </FormControl>
        {/* Policy nr */}
        {type === 'CURRENT-INSURANCE' && (
          <FormControl isInvalid={!!errors.policy_nr}>
            <FormLabel>
              {t(`ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_POLICY-NR`)}
            </FormLabel>
            <Input
              {...register('policy_nr', {
                onChange: errors.name ? () => trigger('policy_nr') : undefined,
                onBlur: () => setFocusedInput(null),
              })}
              onFocus={() => setFocusedInput('policy_nr')}
            />
            {errors.policy_nr?.message && (
              <FormErrorMessage>{t(errors.policy_nr.message)}</FormErrorMessage>
            )}
          </FormControl>
        )}
        <InsuranceCompaniesInput />
        <RiskObjectInputComponent />
        {/* Start date */}
        <Controller
          name="start_date"
          control={control}
          render={({
            field: { onChange, value, ...fieldProps },
            fieldState: { error },
          }) => (
            <FormControl isInvalid={!!error}>
              <FormLabel>
                {t(
                  'ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_START-DATE',
                )}
              </FormLabel>
              <DateInput
                {...fieldProps}
                onChange={({ value }: Change) => onChange(value)}
                initialValue={value}
                onBlur={() => setFocusedInput(null)}
                onFocus={() => setFocusedInput('start_date')}
              />
              {error?.message && (
                <FormErrorMessage>{t(error.message)}</FormErrorMessage>
              )}
            </FormControl>
          )}
        />
        {/* Expiration date */}
        <Controller
          name="due_date"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormControl isInvalid={!!error}>
              <FormLabel>
                {t(
                  'ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_EXPIRATION-DATE',
                )}
              </FormLabel>
              <DateInputNoYear
                onChange={({ value }: Change) => {
                  onChange(value);
                }}
                initialValue={value}
                onBlur={() => setFocusedInput(null)}
                onFocus={() => setFocusedInput('due_date')}
              />
              {error?.message && (
                <FormErrorMessage>{t(error.message)}</FormErrorMessage>
              )}
            </FormControl>
          )}
        />
        {type === 'CURRENT-INSURANCE' && (
          <>
            <Controller
              name="closed_at_distribution"
              control={control}
              render={({
                field: { value, ...fieldProps },
                fieldState: { error },
              }) => {
                return (
                  <FormControl isInvalid={!!error}>
                    <FormLabel>
                      {t(
                        'ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_ALREADY-INSURED',
                      )}
                    </FormLabel>
                    <LabeledSwitch
                      onLabel={t('Yes')}
                      offLabel={t('No')}
                      {...fieldProps}
                      isChecked={value}
                    />
                    {error?.message && (
                      <FormErrorMessage>{t(error.message)}</FormErrorMessage>
                    )}
                  </FormControl>
                );
              }}
            />
          </>
        )}
      </Grid>
    </VStack>
  );
};

export default AddInsuranceModalInsuranceFormGeneral;
